import {
  type AccountingPlatform,
  accountingFieldOrdering,
  type CategoryCode,
  dayMs,
  type MemberRole,
} from "./constants";
import type {
  AccountingFieldType,
  ApprovalPolicyType,
  BudgetGroupType,
  BudgetType,
  CardType,
  ExtendedTransactionType,
  RefreshIntervalEnumType,
  RestrictionType,
  TransactionAccountingDataFieldSelectionType,
  TransactionAccountingFieldOptionType,
  TransactionAccountingSplitFieldSelectionType,
  TransactionAccountingSplitType,
} from "./db.types";
import type { UserWithRole } from "./types";
import type { ApprovalObject } from "./validators";

//* --- Auth Utils ---

// Used to immediately classify the id of an entity
export function classifyId(id: string) {
  return (
    {
      bgg: "budget_group",
      bug: "budget",
      pcd: "physical_card",
      vcd: "virtual_card",
    }[id.substring(0, 3)] ?? "unknown"
  );
}

export function satisfiesRole(current: MemberRole | null, needed: MemberRole) {
  // If the user has no role or explicitly "none", they have no permissions
  if (current === "none" || !current) {
    return false;
  }

  // Owners have full access to everything
  if (current === "owner") {
    return true;
  }

  // Admins have access to everything except owner-specific actions
  if (current === "admin") {
    return needed !== "owner";
  }

  if (current === "bookkeeper") {
    return (
      needed === "viewOnlyAdmin" ||
      needed === "member" ||
      needed === "bookkeeper"
    );
  }

  // viewOnlyAdmin can view everything but can't modify, so they satisfy
  // viewOnlyAdmin and member roles
  if (current === "viewOnlyAdmin") {
    return needed === "viewOnlyAdmin" || needed === "member";
  }

  // Members only have member-level access
  return needed === "member";
}

/**
 * An owner can modify anyone's role, an admin can modify anyone's role except for other admins
 */
export function canModifyRole({
  toModifyRole,
  owner,
  admin,
}: {
  toModifyRole: MemberRole;
  admin: boolean;
  owner: boolean;
}) {
  if (owner) return true;
  if (!admin) return false;

  return toModifyRole !== "admin";
}

//* --- Date Utils ---

/**
 * Get a date arbitrarily far in the future (effectively never)
 * @returns Date
 */
export function getNeverDate() {
  return new Date(2100, 11, 31);
}

type TimeIntervals = "hours" | "days" | "weeks";
type TimeExpression = `${number} ${TimeIntervals}`;
export function getSecondsFromNow(seconds: number | TimeExpression) {
  const now = new Date();

  let totalSeconds: number;

  if (typeof seconds === "number") {
    totalSeconds = seconds;
  } else {
    // Parse time expression like "3 days", "2 hours", "1 weeks"
    const parts = seconds.split(" ");
    const value = Number(parts[0]);
    const unit = parts[1] as TimeIntervals;

    switch (unit) {
      case "hours":
        totalSeconds = value * 60 * 60;
        break;
      case "days":
        totalSeconds = value * 60 * 60 * 24;
        break;
      case "weeks":
        totalSeconds = value * 60 * 60 * 24 * 7;
        break;
      default:
        throw new Error(`Unsupported time unit: ${unit}`);
    }
  }

  // Convert from seconds to milliseconds
  return new Date(now.getTime() + totalSeconds * 1000);
}

type Interval =
  | "quarterly"
  | "annual"
  | "semiannual"
  | "manual"
  | RefreshIntervalEnumType;

/**
 * Gets the start or end date for a given interval based on a reference date
 *
 * @param interval - The interval type (daily, weekly, monthly, quarterly, annual)
 * @param barrierType - Whether to get the start or end of the interval
 * @param date - Optional date to calculate from (defaults to current date)
 * @returns Date object representing the start or end of the interval
 */
export function getIntervalBarrierDate(
  interval: Interval,
  barrierType: "start" | "end",
  dateInput = new Date(),
) {
  // Create a new date to avoid modifying the input date
  const date = new Date(dateInput);

  if (interval === "daily") {
    // For daily interval
    if (barrierType === "start") {
      // Start of day: Set to midnight today
      date.setHours(0, 0, 0, 0);
    } else {
      // End of day: Set to 23:59:59.999 today
      date.setHours(23, 59, 59, 999);
    }
  } else if (interval === "weekly") {
    // Get the day of the week (0 = Sunday, 6 = Saturday)
    const day = date.getDay();

    if (barrierType === "start") {
      // Start of week: Go back to Sunday (start of week)
      date.setDate(date.getDate() - day);
      date.setHours(0, 0, 0, 0);
    } else {
      // End of week: Go forward to Saturday (end of week)
      date.setDate(date.getDate() + (6 - day));
      date.setHours(23, 59, 59, 999);
    }
  } else if (interval === "monthly") {
    if (barrierType === "start") {
      // Start of month: Set to 1st day of current month
      date.setDate(1);
      date.setHours(0, 0, 0, 0);
    } else {
      // End of month: Set to last day of current month
      // Go to first day of next month, then subtract 1 day
      date.setMonth(date.getMonth() + 1, 0);
      date.setHours(23, 59, 59, 999);
    }
  } else if (interval === "quarterly") {
    const month = date.getMonth();
    // Determine current quarter (0-2 = Q1, 3-5 = Q2, 6-8 = Q3, 9-11 = Q4)
    const quarter = Math.floor(month / 3);

    if (barrierType === "start") {
      // Start of quarter: First day of first month in quarter
      date.setMonth(quarter * 3, 1);
      date.setHours(0, 0, 0, 0);
    } else {
      // End of quarter: Last day of last month in quarter
      date.setMonth((quarter + 1) * 3, 0);
      date.setHours(23, 59, 59, 999);
    }
  } else if (interval === "semiannual") {
    const month = date.getMonth();
    // Determine current half (0-5 = H1, 6-11 = H2)
    const half = Math.floor(month / 6);

    if (barrierType === "start") {
      // Start of half: First day of first month in half (Jan 1 or Jul 1)
      date.setMonth(half * 6, 1);
      date.setHours(0, 0, 0, 0);
    } else {
      // End of half: Last day of last month in half (Jun 30 or Dec 31)
      date.setMonth((half + 1) * 6, 0);
      date.setHours(23, 59, 59, 999);
    }
  } else if (interval === "annual") {
    if (barrierType === "start") {
      // Start of year: January 1st of current year
      date.setMonth(0, 1);
      date.setHours(0, 0, 0, 0);
    } else {
      // End of year: December 31st of current year
      date.setMonth(11, 31);
      date.setHours(23, 59, 59, 999);
    }
  }

  return date;
}

/**
 * Calculates the timestamp of when the specified interval ends
 * @param interval - The interval type (daily, weekly, monthly, quarterly, annual)
 * @returns
 */
export function getIntervalEndDate(interval: Interval) {
  if (interval === "lifetime" || interval === "manual") {
    return getNeverDate();
  }
  return getIntervalBarrierDate(interval, "end", new Date());
}

/**
 * Calculates the start and end timestamp of a given interval from a reference date
 * @param interval - The interval type (daily, weekly, monthly, quarterly, annual)
 * @param date - Optional date to calculate from (defaults to current date)
 * @returns
 */
export function getDateRangeBucket(interval: Interval, date = new Date()) {
  return {
    end: getIntervalBarrierDate(interval, "end", date),
    start: getIntervalBarrierDate(interval, "start", date),
  };
}

/**
 * Check if a testDate is within the same "day", "week", "month", etc. as the current date
 * @param interval
 * @param testDate
 * @param date - Optional date to calculate from (defaults to current date)
 * @returns A boolean indicating if the testDate is within the interval of the date
 */
export function inRangeBucket(
  interval: Interval,
  testDate: Date,
  date = new Date(),
) {
  if (interval === "lifetime") return true;
  const { start, end } = getDateRangeBucket(interval, date);

  return start <= testDate && testDate <= end;
}

type Period = "month" | "quarter" | "half-year";

export function formatDateToPeriod(date: Date, period: Period): string {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-indexed (0 = January)

  switch (period) {
    case "month":
      return date.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });

    case "quarter":
      return `Q${Math.floor(month / 3) + 1} ${year}`;

    case "half-year":
      return `H${month < 6 ? 1 : 2} ${year}`;

    default:
      throw new Error(
        `Invalid period: ${period}. Must be 'month', 'quarter', or 'half-year'`,
      );
  }
}

export function formatDateRange(
  startDate: Date,
  endDate: Date,
  forceLongVersion?: boolean,
): string {
  return `${startDate.toLocaleDateString("en-US", {
    day: "numeric",
    month: forceLongVersion ? "long" : "short",
    year: "numeric",
  })} – ${endDate.toLocaleDateString("en-US", {
    day: "numeric",
    month: forceLongVersion ? "long" : "short",
    year: "numeric",
  })}`;
}

export type PeriodBucket = {
  name: string;
  start: Date;
  end: Date;
};

/**
 * Generates all valid periods since the organization's start date
 * @param startDate - The date when the organization was created
 * @param period - The period type (month, quarter, half-year)
 * @returns Array of period objects with name, start, and end dates
 */
export function getPeriodsFromStartDate(
  startDate: Date,
  period: Period,
): PeriodBucket[] {
  const periods: PeriodBucket[] = [];
  const currentDate = new Date();

  const intervalMap: Record<Period, "monthly" | "quarterly" | "semiannual"> = {
    "half-year": "semiannual",
    month: "monthly",
    quarter: "quarterly",
  };

  const interval = intervalMap[period];

  // Convert startDate to UTC for consistent comparison
  const utcStartDate = new Date(
    Date.UTC(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
    ),
  );

  let currentPeriodStart = getIntervalBarrierDate(
    interval,
    "start",
    utcStartDate,
  );

  while (currentPeriodStart <= currentDate) {
    const periodEnd = getIntervalBarrierDate(
      interval,
      "end",
      currentPeriodStart,
    );

    // Ensure both dates are properly set to UTC
    const utcStart = new Date(
      Date.UTC(
        currentPeriodStart.getFullYear(),
        currentPeriodStart.getMonth(),
        currentPeriodStart.getDate(),
        0,
        0,
        0,
        0,
      ),
    );

    const utcEnd = new Date(
      Date.UTC(
        periodEnd.getFullYear(),
        periodEnd.getMonth(),
        periodEnd.getDate(),
        23,
        59,
        59,
        999,
      ),
    );

    periods.push({
      end: utcEnd,
      name: formatDateToPeriod(currentPeriodStart, period),
      start: utcStart,
    });

    // Move to next period (same logic as before)
    if (period === "month") {
      currentPeriodStart = new Date(currentPeriodStart);
      currentPeriodStart.setMonth(currentPeriodStart.getMonth() + 1);
    } else if (period === "quarter") {
      currentPeriodStart = new Date(currentPeriodStart);
      currentPeriodStart.setMonth(currentPeriodStart.getMonth() + 3);
    } else if (period === "half-year") {
      currentPeriodStart = new Date(currentPeriodStart);
      currentPeriodStart.setMonth(currentPeriodStart.getMonth() + 6);
    }

    currentPeriodStart = getIntervalBarrierDate(
      interval,
      "start",
      currentPeriodStart,
    );
  }

  periods.sort((a, b) => (a.end > b.end ? -1 : 1));
  return periods;
}

/**
 * Determines the period type and formats it based on start and end dates
 * Similar to getPeriodsFromStartDate but works backwards from date range
 * @param startDate - The start date of the period (UTC)
 * @param endDate - The end date of the period (UTC)
 * @returns Formatted period string (e.g., "May 2025", "Q2 2025", "H1 2025")
 */
export function getPeriodFromDateRange(
  startDate: Date | string,
  endDate: Date | string,
): string {
  // Parse dates as UTC to match server dates
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Convert to UTC for consistent comparison
  const utcStart = new Date(
    Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()),
  );
  const utcEnd = new Date(
    Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()),
  );

  // Calculate the difference in days
  const diffInMs = utcEnd.getTime() - utcStart.getTime();
  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end days

  // Check if it's a month period (28-31 days)
  if (diffInDays >= 28 && diffInDays <= 31) {
    // Check if this spans exactly one calendar month
    const startMonth = utcStart.getUTCMonth();
    const startYear = utcStart.getUTCFullYear();
    const endMonth = utcEnd.getUTCMonth();
    const endYear = utcEnd.getUTCFullYear();

    // Must be same month and year, or consecutive months
    if (
      (startYear === endYear && startMonth === endMonth) ||
      (startYear === endYear && endMonth === startMonth + 1) ||
      (startYear === endYear - 1 && startMonth === 11 && endMonth === 0)
    ) {
      // Create expected month boundaries
      const expectedStart = new Date(Date.UTC(startYear, startMonth, 1));
      const expectedEnd = new Date(Date.UTC(startYear, startMonth + 1, 0));

      if (
        isSameDayUTC(utcStart, expectedStart) &&
        isSameDayUTC(utcEnd, expectedEnd)
      ) {
        return formatDateToPeriod(utcStart, "month");
      }
    }
  }

  // Check if it's a quarter period (~90-92 days)
  if (diffInDays >= 89 && diffInDays <= 92) {
    // Verify it's actually a full quarter
    const month = utcStart.getUTCMonth();
    const quarterStartMonth = Math.floor(month / 3) * 3;
    const quarterStart = new Date(
      Date.UTC(utcStart.getUTCFullYear(), quarterStartMonth, 1),
    );
    const quarterEnd = new Date(
      Date.UTC(utcStart.getUTCFullYear(), quarterStartMonth + 3, 0),
    );

    if (
      isSameDayUTC(utcStart, quarterStart) &&
      isSameDayUTC(utcEnd, quarterEnd)
    ) {
      return formatDateToPeriod(utcStart, "quarter");
    }
  }

  // Check if it's a half-year period (~180-184 days)
  if (diffInDays >= 178 && diffInDays <= 184) {
    // Verify it's actually a full half-year
    const month = utcStart.getUTCMonth();
    const halfStartMonth = month < 6 ? 0 : 6;
    const halfStart = new Date(
      Date.UTC(utcStart.getUTCFullYear(), halfStartMonth, 1),
    );
    const halfEnd = new Date(
      Date.UTC(utcStart.getUTCFullYear(), halfStartMonth + 6, 0),
    );

    if (isSameDayUTC(utcStart, halfStart) && isSameDayUTC(utcEnd, halfEnd)) {
      return formatDateToPeriod(utcStart, "half-year");
    }
  }

  // If it doesn't match standard periods, fall back to date range
  return `${formatDate(utcStart, "mm-dd-yyyy")} - ${formatDate(utcEnd, "mm-dd-yyyy")}`;
}

/**
 * Helper function to check if two UTC dates are the same day
 */
function isSameDayUTC(date1: Date, date2: Date): boolean {
  return (
    date1.getUTCFullYear() === date2.getUTCFullYear() &&
    date1.getUTCMonth() === date2.getUTCMonth() &&
    date1.getUTCDate() === date2.getUTCDate()
  );
}

/**
 *  Parses a date string in "YYYY-MM-DD" format as a local date (not UTC)
 */
export function parseLocalDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

//* --- Formatting Utils ---

export function stripCountryPrefix(
  phoneNumber: string,
  countryCode: string,
): string {
  // Ensure the phone number starts with '+'
  if (!phoneNumber.startsWith("+")) {
    return phoneNumber;
  }

  // Create regex to match the prefix
  const regex = new RegExp(`^\\+${countryCode}`);

  // Remove the prefix if it matches
  if (regex.test(phoneNumber)) {
    return phoneNumber.replace(regex, "");
  }

  // Return the original if no match
  return phoneNumber;
}

export function getTimestampForFilename(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}_${hours}-${minutes}`;
}

export function formatCurrency(
  amountInCents: number | string,
  showCents = true,
): string {
  // Convert to number and divide by 100 to convert cents to dollars
  const amountInDollars = Number(amountInCents) / 100;

  return new Intl.NumberFormat("en-US", {
    currency: "USD",
    maximumFractionDigits: showCents ? 2 : 0,
    minimumFractionDigits: showCents ? 2 : 0,
    style: "currency",
  }).format(amountInDollars);
}

type DateFormat = "full" | "mm-dd-yyyy" | "date-time";
export function formatDate(
  date: Date | string | number,
  format: DateFormat = "full",
  opts: Intl.DateTimeFormatOptions = {},
) {
  if (!date) {
    return null;
  }
  if (format === "mm-dd-yyyy") {
    return new Intl.DateTimeFormat("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      ...opts,
    })
      .format(new Date(date))
      .replace(/\//g, "-");
  } else if (format === "date-time") {
    return new Intl.DateTimeFormat("en-US", {
      day: "numeric",
      hour: "numeric",
      hour12: true,
      minute: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(date));
  }

  return new Intl.DateTimeFormat("en-US", {
    day: opts.day ?? "numeric",
    month: opts.month ?? "long",
    year: opts.year ?? "numeric",
    ...opts,
  }).format(new Date(date));
}

/**
 * Formats an E.164 US phone number to a readable format with parentheses around area code
 *
 * @param phoneNumber - Phone number in E.164 format (e.g., +19898989999)
 * @returns Formatted phone number like (989) 898-9999 or empty string if invalid
 */
export function formatUSPhone(phoneNumber: string | undefined): string {
  // Return empty string if null or undefined
  if (!phoneNumber) {
    return "";
  }

  // Strip all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, "");

  // Check if this is a US number (should be 11 digits starting with 1)
  if (cleaned.length !== 11 || cleaned[0] !== "1") {
    return phoneNumber; // Return original if not a valid US number
  }

  // Extract the parts
  const areaCode = cleaned.substring(1, 4);
  const prefix = cleaned.substring(4, 7);
  const lineNumber = cleaned.substring(7, 11);

  // Format as (XXX) XXX-XXXX
  return `(${areaCode}) ${prefix}-${lineNumber}`;
}

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getInitials(first: string, last: string) {
  if (!first || !last || first.length < 1 || last.length < 1) {
    return "";
  }
  return first.charAt(0).toUpperCase() + last.charAt(0).toUpperCase();
}

/**
 * Converts a standardized interval type into a natural language current period phrase
 * @param interval - The interval type to convert
 * @returns A natural language representation of the current period
 */
export function intervalToCurrentPeriod(interval: string): string {
  switch (interval) {
    case "daily":
      return "Today";
    case "weekly":
      return "This Week";
    case "monthly":
      return "This Month";
    case "quarterly":
      return "This Quarter";
    case "semiannual":
      return "This Half";
    case "annual":
      return "This Year";
    case "lifetime":
      return "All Time";
    case "manual":
      return "Until Reset";
    default:
      return `This ${interval.charAt(0).toUpperCase() + interval.slice(1)}`;
  }
}

/**
 * Formats a file size in bytes to a human readable string
 *
 * @param bytes - The size in bytes to format
 * @param decimals - Number of decimal places to include (default: 2)
 * @returns A formatted string representation of the size (e.g., "1.5 MB")
 */
export function formatFileSize(bytes: number, decimals = 2): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Returns a user-friendly explanation for a transaction decline reason
 * @param reason The reason code for the declined transaction
 * @returns A plain text explanation of why the transaction was declined
 */
export function getTransactionDeclineExplanation(
  reason: string | null,
): string {
  switch (reason) {
    case "invalidPhysicalCard":
      return "The physical card details provided are invalid or cannot be processed.";

    case "invalidMerchant":
      return "This merchant is not authorized to process this type of transaction.";

    case "cvv2Mismatch":
      return "The security code (CVV) entered does not match what we have on file.";

    case "expirationMismatch":
      return "The expiration date provided does not match what we have on file.";

    case "timeout":
      return "The payment system took too long to respond. Please try again.";

    case "cardFrozen":
      return "Your card is frozen. All transactions will be declined until it is unfrozen.";

    case "cardCanceled":
      return "This card has been canceled and can no longer be used for transactions.";

    case "cardInactive":
      return "This card has not been activated. Please activate your card before using it.";

    case "cardReplaced":
      return "This card has been replaced with a new card. Please use your new card.";

    case "cardLimitExceeded":
      return "This transaction would exceed the spending limit for this card.";

    case "accountLimitExceeded":
      return "This transaction would exceed the spending limit for your account.";

    case "accountLocked":
      return "Your account has been locked for security reasons. Please contact customer support.";

    case "referToCardIssuer":
      return "Please contact your card issuer for more information about this decline.";

    case "physicalCardDisabled":
      return "Physical card transactions have been disabled for this card.";

    case "standInProcessing":
      return "The transaction was processed using backup systems and was declined for safety reasons.";

    case "missingOriginalAuthorization":
      return "The original authorization for this transaction could not be found.";

    case "mccRestrictions":
      return "This type of merchant category is restricted for your card.";

    case "merchantRestrictions":
      return "Transactions with this specific merchant are restricted for your card.";

    case "suspectedFraud":
      return "This transaction was flagged as potentially fraudulent. Please contact customer support if this is a legitimate transaction.";

    default:
      return "An unexpected error occurred. Please try again or contact customer support.";
  }
}

export function formatCardNameAndLast4(card: CardType | null) {
  return `${card?.name} •••• ${card?.last4}`;
}

//* --- Array and Data Comparison Utils ---

/**
 * Compares original and new arrays to determine which items to add and delete
 */
export function generateArrayDiffWithId<T extends { id: string }>(
  originalItems: T[],
  newItems: T[],
): {
  toAdd: T[];
  toDelete: T[];
} {
  // Create Sets of IDs for efficient lookup
  const originalIds = new Set(originalItems.map((receipt) => receipt.id));
  const newIds = new Set(newItems.map((receipt) => receipt.id));

  // Find items to add (in new but not in original)
  const toAdd = newItems.filter((receipt) => !originalIds.has(receipt.id));

  // Find items to delete (in original but not in new)
  const toDelete = originalItems.filter((receipt) => !newIds.has(receipt.id));

  return { toAdd, toDelete };
}

/**
 * Array diff function with strings instead of keys on an object
 */
export function generateArrayStringDiff<T extends string>(
  originalItems: T[],
  newItems: T[],
): {
  toAdd: T[];
  toDelete: T[];
} {
  // Create Sets of IDs for efficient lookup
  const originalIds = new Set(originalItems.map((item) => item));
  const newIds = new Set(newItems.map((item) => item));

  // Find items to add (in new but not in original)
  const toAdd = newItems.filter((item) => !originalIds.has(item));

  // Find items to delete (in original but not in new)
  const toDelete = originalItems.filter((item) => !newIds.has(item));

  return { toAdd, toDelete };
}

/**
 * Compares two arrays of primitive values for equality regardless of order
 *
 * @param a First array to compare
 * @param b Second array to compare
 * @returns True if arrays contain the same primitive values (ignoring order)
 */
export function arePrimitiveArraysEqualIgnoreOrder<
  T extends string | number | boolean,
>(a: T[], b: T[]): boolean {
  // Check if lengths are different
  if (a.length !== b.length) {
    return false;
  }

  // Sort and compare
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();

  // Compare each element
  for (let i = 0; i < sortedA.length; i++) {
    if (sortedA[i] !== sortedB[i]) {
      return false;
    }
  }

  return true;
}

/**
 * Compares two objects containing primitive values for equality
 * This only compares one level deep (not nested objects)
 *
 * @param a First object to compare
 * @param b Second object to compare
 * @returns True if objects have identical keys and values
 */
export function arePrimitiveObjectsEqual<T extends Record<string, any>>(
  a: T,
  b: T,
): boolean {
  // Check if both are objects
  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  ) {
    return false;
  }

  // Get keys from both objects
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  // Check if they have the same number of keys
  if (keysA.length !== keysB.length) {
    return false;
  }

  // Check if all keys from A exist in B and have the same primitive value
  return keysA.every((key) => Object.hasOwn(b, key) && a[key] === b[key]);
}

//* --- Card/Budget/Transaction Utils ---

/**
 * Returns if a transaction is settled or pending. Only such settled or pending transactions can be classified into a budget and have requirements enforced
 * @param status Transaction Status
 */
export function isTransactionValid(
  status: "pending" | "declined" | "settled" | "rejected" | "voided",
) {
  return status === "pending" || status === "settled";
}

/**
 * For a given transaction, return if the transaction can be classified to a budget which allows only provided categories
 */
export function isCategoryAllowed(
  transactionCategory: CategoryCode,
  allowedCategories: CategoryCode[],
) {
  if (allowedCategories.length === 0) {
    return true;
  }
  return allowedCategories.includes(transactionCategory);
}

/**
 * Returns if a transaction is allowed to be classified to a budget. If not, provides a reason. Checks for transaction categories, funds, and if the budget is active
 */
export function getDisabledReason(
  budget: BudgetType & { budgetGroup: BudgetGroupType },
) {
  if (budget.budgetGroup.status === "archived") {
    return {
      allowed: false,
      reason: "Budget archived",
    };
  }
  if (budget.budgetGroup.status === "frozen") {
    return {
      allowed: false,
      reason: "Budget frozen",
    };
  }
  return {
    allowed: true,
    reason: "",
  };
}

export enum IncompleteReason {
  MISSING_BUDGET = "Missing budget",
  MISSING_MEMO = "Missing memo",
  MISSING_RECEIPT = "Missing receipt",
}

// ExtendedTransactionType is now defined in db.types.ts

export function getTransactionIncompleteReason(
  trx: ExtendedTransactionType,
): IncompleteReason | null {
  if (!trx.card || !trx.policy) return null;

  const policyCheckData: PolicyCheckData = {
    card: trx.card,
    hasReceipt: trx.receipts.length > 0,
    policy: trx.policy,
    transaction: trx,
  };

  if (!checkBudgetCompliance(policyCheckData).isSatisfied)
    return IncompleteReason.MISSING_BUDGET;
  if (!checkMemoCompliance(policyCheckData).isSatisfied)
    return IncompleteReason.MISSING_MEMO;
  if (!checkReceiptCompliance(policyCheckData).isSatisfied)
    return IncompleteReason.MISSING_RECEIPT;

  return null;
}

export function getBudgetReportFileName(
  budgetName: string,
  startDate: Date,
  endDate: Date,
): string {
  return `${budgetName} Report (${formatDateRange(startDate, endDate, true)}).pdf`;
}

//* --- Policy Utils ---

type PolicyFields = {
  receiptCents: number | null;
  memoCents: number | null;
  excludeVirtualReceipt: boolean;
  requireBudget: boolean;
} | null;

type TransactionFields = {
  amountCents: number;
  memo: string | null;
  exemptReceipts: boolean;
  budgetId: string | null;
};

type CardFields = {
  type: "virtual" | "physical";
};

export type PolicyCheckData = {
  policy: PolicyFields;
  transaction: TransactionFields;
  card: CardFields;
  hasReceipt: boolean;
};

export function checkBudgetCompliance({
  policy,
  transaction,
}: PolicyCheckData) {
  const isPresent = transaction.budgetId !== null;

  if (policy == null) {
    return {
      isPresent,
      isRequiredByPolicy: false,
      isSatisfied: true,
    };
  }

  return {
    isPresent,
    isRequiredByPolicy: policy.requireBudget,
    isSatisfied: !policy.requireBudget || isPresent,
  };
}

export function checkReceiptCompliance({
  policy,
  transaction,
  hasReceipt,
  card,
}: PolicyCheckData) {
  const isPresent = hasReceipt;

  // No policy means no requirement
  if (policy === null) {
    return {
      isPresent,
      isRequiredByPolicy: false,
      isSatisfied: true,
    };
  }

  // Check if receipt is required by policy
  // Check if this is a virtual card that should be excluded from receipt requirements
  const isVirtualCardExcluded =
    policy.excludeVirtualReceipt && card.type === "virtual";

  const isRequiredByPolicy =
    policy.receiptCents !== null &&
    policy.receiptCents <= transaction.amountCents &&
    !isVirtualCardExcluded;

  // If required by policy, satisfaction depends on presence
  // If not required by policy, it's always satisfied
  const isSatisfied =
    !isRequiredByPolicy || isPresent || transaction.exemptReceipts;

  return {
    isPresent,
    isRequiredByPolicy,
    isSatisfied,
  };
}

export function checkMemoCompliance({ policy, transaction }: PolicyCheckData) {
  const isPresent = transaction.memo !== null && transaction.memo.length > 0;

  // No policy means no requirement
  if (policy === null) {
    return {
      isPresent,
      isRequiredByPolicy: false,
      isSatisfied: true,
    };
  }

  // Check if memo is required by policy
  const isRequiredByPolicy =
    policy.memoCents !== null && policy.memoCents <= transaction.amountCents;

  // If required by policy, satisfaction depends on presence
  // If not required by policy, it's always satisfied
  const isSatisfied = !isRequiredByPolicy || isPresent;

  return {
    isPresent,
    isRequiredByPolicy,
    isSatisfied,
  };
}

export function isExpensePolicySatisfied(data: PolicyCheckData) {
  return (
    checkMemoCompliance(data).isSatisfied &&
    checkReceiptCompliance(data).isSatisfied &&
    checkBudgetCompliance(data).isSatisfied
  );
}

//* --- Form Utils ---

/**
 * Strips out all uneditable fields on a restriction object
 * @param restriction A restriction object returned from the database
 * @returns Default values for a form which allows editing of restrictions
 */
export function getDefaultFormValuesFromRestriction(
  restriction: RestrictionType,
) {
  const {
    id: _id,
    lifetimeBalanceCents: _lifetimeBalanceCents,
    lifetimeLimitCents: _lifetimeLimitCents,
    organizationId: _organizationId,
    periodBalanceCents: _periodBalanceCents,
    refreshAt: _refreshAt,
    ...editable
  } = restriction;
  return editable;
}

export function getDaysInRange(start: Date, end: Date) {
  const date = new Date(start);
  const dates = [];

  while (date.getTime() <= end.getTime()) {
    dates.push(new Date(date));
    date.setTime(date.getTime() + dayMs);
  }

  return dates;
}

export function startsWithVowelSound(word: string) {
  // Common English words that start with 'h' but the 'h' is silent
  const silentHWords = new Set(["hour", "honest", "honor", "heir"]);

  // Words that start with 'u' but have a 'yu' sound
  const yuSoundWords = new Set([
    "useful",
    "university",
    "union",
    "unique",
    "united",
  ]);

  const trimmedWord = word.toLowerCase().trim();

  // Handle empty string
  if (!trimmedWord) return false;

  // Handle silent h words
  if (silentHWords.has(trimmedWord)) return true;

  // Handle special u cases
  if (yuSoundWords.has(trimmedWord)) return false;

  // Test for vowel sound at start
  return /^[aeiou]/i.test(trimmedWord);
}

export function isApprovalObjectSatisfied(
  approver: ApprovalObject,
  approvedBy: UserWithRole[],
): boolean {
  if (
    approver.type === "role" &&
    approvedBy.some((user) => satisfiesRole(user.role, approver.role))
  ) {
    return true;
  } else if (
    approver.type === "member" &&
    approvedBy.some((user) => user.id === approver.userId)
  ) {
    return true;
  }

  return false;
}

export function isApprovalPolicySatisfied(
  approvalPolicy: ApprovalPolicyType,
  approvedBy: UserWithRole[],
): boolean {
  return approvalPolicy.groupOperator === "any"
    ? approvalPolicy.approvers.some((approver) =>
        isApprovalObjectSatisfied(approver, approvedBy),
      )
    : approvalPolicy.approvers.every((approver) =>
        isApprovalObjectSatisfied(approver, approvedBy),
      );
}

export function canApprove(approvers: ApprovalObject[], user: UserWithRole) {
  return approvers.some((approver) =>
    isApprovalObjectSatisfied(approver, [user]),
  );
}

//* --- Accounting Utils ---

/**
 * Sorts accounting fields based on platform-specific ordering defined in constants
 * Fields not found in the ordering are placed at the end, sorted alphabetically
 */
export function sortAccountingFields<T extends { name: string }>(
  fields: T[],
  platform: AccountingPlatform,
): T[] {
  // Import here to avoid circular dependency issues
  const ordering = accountingFieldOrdering[platform];

  return [...fields].sort((a, b) => {
    const aIndex = ordering.indexOf(a.name as any);
    const bIndex = ordering.indexOf(b.name as any);

    // If both fields are in the ordering, sort by their order
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    // If only one field is in the ordering, it comes first
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    // If neither field is in the ordering, sort alphabetically
    return a.name.localeCompare(b.name);
  });
}

export function validateFieldRequirements({
  fields,
  fieldSelections,
  splits,
}: {
  fields: AccountingFieldType[];
  fieldSelections: TransactionAccountingDataFieldSelectionType[];
  splits: Array<
    TransactionAccountingSplitType & {
      fieldSelections: TransactionAccountingSplitFieldSelectionType[];
    }
  >;
}): string[] {
  const missingFields: string[] = [];

  // Loop through the accounting fields
  for (const field of fields) {
    // Only perform a check if the requirements level is "alwaysRequired" or "required"
    if (
      field.requirementLevel !== "alwaysRequired" &&
      field.requirementLevel !== "required"
    ) {
      continue;
    }

    if (field.assignmentLevel === "topLevel") {
      // Check for a selection existing within the fieldSelections
      const hasSelection = fieldSelections.some(
        (selection) => selection.accountingFieldId === field.id,
      );

      if (!hasSelection) {
        missingFields.push(field.displayName || field.name);
      }
    } else if (field.assignmentLevel === "lineItem") {
      // Check for a selection within the fieldSelections of each split
      for (let i = 0; i < splits.length; i++) {
        const split = splits[i];
        const hasSelection = split.fieldSelections.some(
          (selection) => selection.accountingFieldId === field.id,
        );

        if (!hasSelection) {
          missingFields.push(
            `${field.displayName || field.name} in split ${i + 1}`,
          );
        }
      }
    }
  }

  return missingFields;
}

export function getFieldOptionDisplayName(
  option: TransactionAccountingFieldOptionType,
) {
  if (option?.additionalProperties?.data?.accountCode) {
    return `${option.additionalProperties.data.accountCode} - ${option.name}`;
  }

  return option.name;
}

export function validateRoutingNumber(routingNumber: string): boolean {
  // Routing number must be 9 digits
  if (!/^\d{9}$/.test(routingNumber)) {
    return false;
  }

  const digits = routingNumber.split("").map(Number);

  const checksum =
    3 * (digits[0]! + digits[3]! + digits[6]!) +
    7 * (digits[1]! + digits[4]! + digits[7]!) +
    1 * (digits[2]! + digits[5]! + digits[8]!);

  return checksum % 10 === 0;
}
